import React, { useCallback, useState } from 'react'
import moment from 'moment'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Hr from 'components/Common/Hr'
import HelpPane from 'components/Common/HelpPane'
import { DAY_MONTH_NAME_SHORT, DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import styled from 'styled-components'
import { rem } from 'polished'
import { SEVEN_DAYS_AFTER_PURCHASE, FOURTEEN_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { getFlexibleCancellationDays } from 'lib/cancellationPolicy'
import Modal from 'components/Luxkit/Modal/Modal'
import LineRefundIcon from 'components/Luxkit/Icons/line/LineRefundIcon'
import TextLink from 'components/Luxkit/TextLink'
import Clickable from 'components/Common/Clickable'
import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import { useTranslation } from 'react-i18next'

const MiddledTooltipIcon = styled(SolidInfoCircleIcon)`
  vertical-align: middle;
`

const CancellationTermsWrapper = styled.div`
  display: grid;
  grid-column-gap: ${rem(18)};
  margin-top: ${rem(40)};

  ${mediaQueryUp.sm} {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;
  }
`

const TermsGrid = styled.div`
  display: grid;
  grid-column-gap: ${rem(16)};
  grid-row-gap: ${rem(18)};
  grid-template-columns: 1fr 2fr;

  ${mediaQueryUp.sm} {
    grid-template-columns: 1fr 1fr;
  }
`

interface Props {
  cancellationPolicy: App.OfferCancellationPolicy;
  propertyTimezoneOffset?: number;
  checkInDate?: string | moment.Moment;
  isFlashUSP?: boolean;
}

function OfferPropertyFlexibleCancellationPolicies(props: Props) {
  const { cancellationPolicy, propertyTimezoneOffset, checkInDate, isFlashUSP } = props
  const [modalOpen, setModalOpen] = useState(false)
  const { t } = useTranslation(['common', 'hotelOfferList'])

  const days = getFlexibleCancellationDays(cancellationPolicy)
  let startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let startBasedOnCheckIn = checkInDate && (propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(days, 'days') : moment(checkInDate).subtract(days, 'days'))

  const changeOfMindDeadline = checkInDate && propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days') : moment(checkInDate).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days')
  startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline

  const cancellationAfterChangeOfMindPeriod = checkInDate && startBasedOnToday.isBefore(changeOfMindDeadline) && moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(moment(checkInDate).subtract(days, 'days'))
  startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

  const onItemClick = useCallback((e) => {
    e.preventDefault()
    setModalOpen(true)
  }, [])

  const onModalClose = useCallback(() => {
    setModalOpen(false)
  }, [])

  return <>
    {!isFlashUSP &&
      <Clickable onClick={onItemClick}>
        <BodyText variant="medium" weight="semi-bold" as="span" colour="highlight">{t('flexibleCancellation', { ns: 'hotelOfferList' })}</BodyText>{' '}
        {!checkInDate && <BodyText variant="medium" as="span">{t('availableHotelOnly', { ns: 'hotelOfferList' })}</BodyText>}
        {checkInDate && <BodyText variant="medium" as="span">{t('untilDate', { ns: 'hotelOfferList', date: startBasedOnCheckIn?.format(DAY_MONTH_NAME_SHORT) })}</BodyText>}
        {' '}<MiddledTooltipIcon size="XS" />
      </Clickable>
    }
    {isFlashUSP &&
      <TextLink
        size="medium"
        variant="highlight"
        underline={false}
        data-type="7day"
        onClick={onItemClick}
        startIcon={<LineRefundIcon />}
      >
        {t('flexibleCancellation', { ns: 'hotelOfferList' })}
      </TextLink>
    }
    <Modal
      onClose={onModalClose}
      isOpen={modalOpen}
      title={t('Cancellation policy', { ns: 'common' })}
    >
      <BodyText variant="medium" as="p">
        {t('cancellationPolicyDescription', { ns: 'hotelOfferList' })}
      </BodyText>
      <CancellationTermsWrapper>
        <TermsGrid>
          {!checkInDate && <BodyText variant="medium" weight="semi-bold" as="span">
            {t('upToSevenDaysAfterPurchase', { ns: 'hotelOfferList' })}
          </BodyText>}
          {checkInDate && <BodyText variant="medium" weight="semi-bold" as="span">
            {t('cancelUntilDate', { ns: 'hotelOfferList', date: startBasedOnToday.format(DMY_CASUAL_SHORT_FORMAT) })}
          </BodyText>}
          <BodyText variant="medium" as="span">
            {t('refund100Percent', { ns: 'hotelOfferList' })}
          </BodyText>
        </TermsGrid>
        <Hr size="md"/>
        {cancellationAfterChangeOfMindPeriod && <>
          <TermsGrid>
            <BodyText variant="medium" weight="semi-bold" as="span">
              {t('cancelUntilDate', { ns: 'hotelOfferList', date: startBasedOnCheckIn?.format(DMY_CASUAL_SHORT_FORMAT) })}
            </BodyText>
            <BodyText variant="medium" as="span">
              {t('refund100PercentToCredit', { ns: 'hotelOfferList' })}
            </BodyText>
          </TermsGrid>
          <Hr size="md"/>
        </>}
        {!cancellationAfterChangeOfMindPeriod && !checkInDate && <>
          <TermsGrid>
            <BodyText variant="medium" weight="semi-bold" as="span">
              {t('upToDaysPriorToCheckIn', { ns: 'hotelOfferList', days: days + 1 })}
            </BodyText>
            <BodyText variant="medium" as="span">
              {t('refund100PercentToCredit', { ns: 'hotelOfferList' })}
            </BodyText>
          </TermsGrid>
          <Hr size="md"/>
        </>}
        <TermsGrid>
          {!checkInDate && <BodyText variant="medium" weight="semi-bold" as="span">
            {t('fromDaysUntilCheckIn', { ns: 'common', days })}
          </BodyText>}
          {checkInDate && <BodyText variant="medium" weight="semi-bold" as="span">
            {t('cancelAfterDate', { ns: 'hotelOfferList', date: moment(startBasedOnCheckIn).format(DMY_CASUAL_SHORT_FORMAT) })}
          </BodyText>}
          <BodyText variant="medium" as="span">
            {t('nonRefundable', { ns: 'hotelOfferList' })}
          </BodyText>
        </TermsGrid>
      </CancellationTermsWrapper>
      <HelpPane />
    </Modal>
  </>
}

export default OfferPropertyFlexibleCancellationPolicies
